/**
 * The app shell: sign in, pick or create today's session, then the entry path.
 *
 * The entry path is the whole product, so it is worth being explicit about the
 * tap budget this implements (FR-ENTRY-12, AC-27):
 *
 *   tap 1 — the winning side's "Won" button
 *   tap 2 — the losing score
 *
 * and that is the game logged. Player names sit on the same card as separate
 * tappable controls (FR-ENTRY-4a, AC-27g), so tapping a name can never be
 * mistaken for declaring a winner. There is no edit mode to enter or leave.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { RequestFailed, api, type Me, type SessionView } from "./api.js";
import { BackupLink, Frame } from "./Frame.js";
import { InstallPrompt } from "./InstallPrompt.js";
import { useSync } from "./sync/useSync.js";
import { Guide } from "./screens/Guide.js";
import { SignIn } from "./screens/SignIn.js";
import { SessionScreen } from "./screens/SessionScreen.js";
import { SessionsScreen } from "./screens/SessionsScreen.js";

/** Survives a reload; cleared when the session is left or turns out to be gone. */
const OPEN_SESSION_KEY = "core-baddy.openSession";

export function App(): React.JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * Remembered across a reload, because a PWA update or an accidental refresh
   * mid-session should not drop the member on the picker. It is only ever a
   * hint: a stale id 404s, is cleared, and the picker appears.
   */
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(OPEN_SESSION_KEY);
    } catch {
      // Private browsing can refuse storage outright. Not worth an error.
      return null;
    }
  });
  const [view, setView] = useState<SessionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  /**
   * FR-HELP-1 — above the auth branch on purpose. "How does this work" is a
   * question people have *before* they have an account, and the guide needs no
   * network, so it must not sit behind either gate.
   */
  const [showGuide, setShowGuide] = useState(false);

  const refreshRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Anything the queue manages to send may have changed the server's view, so
  // a successful flush pulls it again.
  const sync = useSync({ onSynced: () => void refreshRef.current() });

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch(() => {
        // AC-15 - the app launches offline. A cold start with no signal cannot
        // confirm the session, and saying "sign in" would be wrong: the member
        // is signed in, the app simply cannot ask right now.
        setMe(null);
        if (!navigator.onLine) setOffline(true);
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * FR-SESS-6 — on session day the app opens on the active session, with no
   * navigation. Only when there is exactly one is it unambiguous (SESS-07);
   * with two the picker shows both rather than guessing, which is what the
   * club now has for 2 August.
   *
   * Once, and only once. The first version re-ran whenever `sessionId` became
   * null, which is precisely what "Open a different session" does — so the
   * button cleared the session and the effect immediately put it back.
   */
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!me || autoOpened.current) return;
    autoOpened.current = true;
    // Already holding one from last time; do not override the member's choice.
    if (sessionId) return;
    api
      .today()
      .then(({ sessions }) => {
        if (sessions.length === 1) setSessionId(sessions[0].id);
      })
      .catch(() => undefined);
  }, [me, sessionId]);

  // Kept in step with the state rather than written at each call site, so no
  // path can change the session and forget to record it.
  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem(OPEN_SESSION_KEY, sessionId);
      else localStorage.removeItem(OPEN_SESSION_KEY);
    } catch {
      /* storage refused; the app works, it just forgets */
    }
  }, [sessionId]);

  /**
   * Refreshes race, and the loser must not win.
   *
   * Two things ask for a refresh: an action the member took (undo, take out,
   * bring back) and the sync queue finishing a flush. Both fire on the same
   * taps. Without sequencing, an older response that arrives late overwrites a
   * newer one, and the screen shows state the server no longer holds — which
   * is how somebody comes to undo the same game twice.
   *
   * A monotonic counter is enough: a response is applied only if no later
   * request has been issued since it went out.
   */
  const latestRequest = useRef(0);
  const refresh = useCallback(async () => {
    if (!sessionId) return;
    const seq = ++latestRequest.current;
    try {
      const fresh = await api.session(sessionId);
      if (seq !== latestRequest.current) return;
      setView(fresh);
      setError(null);
    } catch (e) {
      if (seq !== latestRequest.current) return;
      if (e instanceof RequestFailed && e.status === 401) {
        setMe(null);
        return;
      }
      // UI-15 — a remembered session that has since been removed must return
      // the member to the picker, not strand them on an error.
      if (e instanceof RequestFailed && e.status === 404) {
        setSessionId(null);
        setView(null);
        setError(null);
        return;
      }
      setError(e instanceof Error ? e.message : "Could not load the session.");
    }
  }, [sessionId]);

  useEffect(() => {
    refreshRef.current = refresh;
    void refresh();
  }, [refresh]);

  /** Signing out, or being signed out by a password change, returns to the start. */
  const signedOut = useCallback(() => {
    setMe(null);
    setSessionId(null);
    setView(null);
    autoOpened.current = false;
  }, []);

  if (showGuide) {
    return (
      <Frame>
        <Guide onClose={() => setShowGuide(false)} />
      </Frame>
    );
  }

  if (loading) {
    return (
      <Frame>
        <main className="centre">Loading…</main>
      </Frame>
    );
  }

  if (!me && offline) {
    return (
      <Frame>
        <main className="centre">
          <div className="card stack">
            <h1>No signal</h1>
            <p>
              Core Baddy opened, but it cannot reach the server to confirm who
              you are. Anything you have already logged is safe on this phone
              and will sync when you get signal.
            </p>
            <BackupLink note="paper equivalent" />
            <button className="link" onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        </main>
      </Frame>
    );
  }

  if (!me) {
    return (
      <Frame>
        <SignIn onSignedIn={setMe} onShowGuide={() => setShowGuide(true)} />
      </Frame>
    );
  }

  if (!sessionId) {
    return (
      <Frame>
        <InstallPrompt />
        <SessionsScreen
          me={me}
          onOpen={setSessionId}
          onSignedOut={signedOut}
          onShowGuide={() => setShowGuide(true)}
        />
      </Frame>
    );
  }

  if (!view) {
    return (
      <Frame>
        <main className="centre">
          {error ? <p className="error">{error}</p> : "Loading session…"}
        </main>
      </Frame>
    );
  }

  return (
    <Frame>
      <SessionScreen
        me={me}
        view={view}
        sync={sync}
        onChanged={refresh}
        onSignedOut={signedOut}
        onLeave={() => {
          setSessionId(null);
          setView(null);
        }}
        /* FR-ADMIN-11 — the attention panel names a session; this is how it
           reaches one. Clearing the view first, so the new session loads
           rather than the old one lingering while its replacement fetches. */
        onOpenSession={(id) => {
          setView(null);
          setSessionId(id);
        }}
        onShowGuide={() => setShowGuide(true)}
        recordGame={async (
          sideA,
          sideB,
          sideAScore,
          sideBScore,
          targetScore,
          gameType,
          courtNumber,
          tournamentFixtureId,
        ) => {
          // Rule L-1 — durable before confirmed. The op carries its own
          // client_uuid, so a retry after a dropped connection cannot create a
          // second game; and if this phone dies now, the result is still here.
          const op = await sync.record("create_game", {
            sessionId,
            sideA,
            sideB,
            sideAScore,
            sideBScore,
            targetScore,
            gameType,
            courtNumber,
            /* FR-TOUR-4 — which fixture this settled, carried in the op so a
               tournament result logged with no signal still knows what it was
               a result of when it finally sends. Undefined for ordinary play,
               which is every other session. */
            tournamentFixtureId,
            playedAt: new Date().toISOString(),
          });
          return op;
        }}
      />
    </Frame>
  );
}
